// components/landing/ContactForm.tsx
// Email contact form for the landing page. Posts to the SiteGenius forms
// backend (POST /__sg/forms) with a honeypot and a Cloudflare Turnstile token,
// following the contract in packages/bridge/src/prompt/forms.ts. Exposes a
// focusEmail() handle and can defer Turnstile until the form nears the screen.

import { useEffect, useId, useImperativeHandle, useRef, useState } from 'react'
import type { FormEvent, Ref, RefObject } from 'react'
import { z } from 'zod'
import { LANDING_PROJECT_ID, TURNSTILE_SITE_KEY } from '@/src/lib/contact'
import { useInView } from '@/src/lib/motion/useInView'
import { useTurnstile } from './useTurnstile'

/** Imperative handle for "Email us" buttons elsewhere on the page. */
export interface ContactFormHandle {
  /** Focus the email field (or the confirmation once sent) without scrolling. */
  focusEmail: () => void
}

export interface ContactFormProps {
  ref?: Ref<ContactFormHandle>
  /**
   * Defer Turnstile until the form is within one viewport of the screen or
   * focusEmail() is called. Default false: the widget loads on mount.
   */
  lazyTurnstile?: boolean
}

// Observed only in lazy mode; a null ref keeps useInView from observing at all.
const NOT_OBSERVED: RefObject<HTMLFormElement | null> = { current: null }

const FORMS_ENDPOINT = '/__sg/forms'
const FORM_NAME = 'contact'

const FormsResponseSchema = z.object({
  ok: z.boolean().optional(),
  error: z.string().optional(),
})

const GENERIC_ERROR = 'Something went wrong. Please try again.'
const SECURITY_CHECK_ERROR = 'Please complete the security check and try again.'
const SECURITY_CHECK_UNAVAILABLE =
  "The security check didn't load. Reload the page and try again, or use WhatsApp or call us."
const NETWORK_ERROR =
  "Couldn't send your message. Check your connection and try again."

const ERROR_MESSAGES: Record<string, string> = {
  'bad-email': 'Please enter a valid email address.',
  'rate-limit':
    'Too many messages in a short time. Please try again in a few minutes.',
  'spam-detected':
    "Your message couldn't be sent. Please use WhatsApp or call us instead.",
  'turnstile-failed': SECURITY_CHECK_ERROR,
  'not-published':
    "The contact form isn't available right now. Please use WhatsApp or call us.",
  'server-error': GENERIC_ERROR,
}

function messageFor(slug: string | undefined): string {
  if (slug !== undefined && Object.hasOwn(ERROR_MESSAGES, slug)) {
    return ERROR_MESSAGES[slug]
  }
  return GENERIC_ERROR
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'error'; message: string }
  | { kind: 'success' }

const FIELD_CLASS =
  'block w-full min-h-11 rounded-md border border-foreground-subtle bg-background px-3.5 py-2.5 text-base text-foreground transition-colors hover:border-foreground-muted focus:border-border-focus'

const LABEL_CLASS = 'mb-2 block text-sm font-medium text-foreground'

function readText(data: FormData, key: string): string {
  const value = data.get(key)
  return typeof value === 'string' ? value : ''
}

export function ContactForm({ ref, lazyTurnstile = false }: ContactFormProps) {
  const ids = useId()
  const nameId = `${ids}-name`
  const emailId = `${ids}-email`
  const messageId = `${ids}-message`

  const [state, setState] = useState<SubmitState>({ kind: 'idle' })
  const formRef = useRef<HTMLFormElement>(null)
  const nearScreen = useInView(lazyTurnstile ? formRef : NOT_OBSERVED, {
    rootMargin: '100% 0px',
    once: true,
  })
  const [requested, setRequested] = useState(false)
  const { containerRef, token, status, reset } = useTurnstile(
    TURNSTILE_SITE_KEY,
    { enabled: !lazyTurnstile || nearScreen || requested },
  )
  const successRef = useRef<HTMLDivElement>(null)
  const submitRef = useRef<HTMLButtonElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)

  useImperativeHandle(
    ref,
    () => ({
      focusEmail() {
        setRequested(true)
        const target = successRef.current ?? emailRef.current
        target?.focus({ preventScroll: true })
      },
    }),
    [],
  )

  useEffect(() => {
    // The form disappears on success: hand focus to the confirmation so
    // keyboard and screen-reader users are not dropped back to the page top.
    if (state.kind === 'success') successRef.current?.focus()
    // The button is disabled while sending, which drops focus: put it back
    // when the attempt fails so the visitor can retry from the keyboard.
    if (state.kind === 'error') submitRef.current?.focus()
  }, [state])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (state.kind === 'sending') return

    if (!token) {
      setState({
        kind: 'error',
        message:
          status === 'error'
            ? SECURITY_CHECK_UNAVAILABLE
            : SECURITY_CHECK_ERROR,
      })
      return
    }

    const data = new FormData(event.currentTarget)
    const fields = {
      name: readText(data, 'name'),
      email: readText(data, 'email'),
      message: readText(data, 'message'),
      website: readText(data, 'website'),
    }

    setState({ kind: 'sending' })

    try {
      const res = await fetch(FORMS_ENDPOINT, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          projectId: LANDING_PROJECT_ID,
          formName: FORM_NAME,
          fields,
          turnstileToken: token,
          sourceUrl: window.location.href,
        }),
      })

      let parsed: z.infer<typeof FormsResponseSchema> = {}
      try {
        const result = FormsResponseSchema.safeParse(await res.json())
        if (result.success) parsed = result.data
      } catch {
        // Non-JSON body (a 404 page, say): treated as a generic failure below.
      }

      if (res.ok && parsed.ok) {
        setState({ kind: 'success' })
        return
      }

      setState({ kind: 'error', message: messageFor(parsed.error) })
    } catch {
      setState({ kind: 'error', message: NETWORK_ERROR })
    }

    // A Turnstile token is single-use: ask for a fresh one after any failure.
    reset()
  }

  if (state.kind === 'success') {
    return (
      <div
        ref={successRef}
        role="status"
        tabIndex={-1}
        className="self-start rounded-md border border-border bg-background p-6 text-lg text-foreground"
      >
        Thanks, we got your message and will reply soon.
      </div>
    )
  }

  const sending = state.kind === 'sending'

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5"
      aria-label="Email us"
    >
      {/* Honeypot: real visitors never see or fill it; crude bots often do. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="sg-honeypot pointer-events-none absolute -left-[9999px] size-px opacity-0"
      />

      <div>
        <label htmlFor={nameId} className={LABEL_CLASS}>
          Name{' '}
          <span className="font-normal text-foreground-muted">(optional)</span>
        </label>
        <input
          id={nameId}
          type="text"
          name="name"
          autoComplete="name"
          className={FIELD_CLASS}
        />
      </div>

      <div>
        <label htmlFor={emailId} className={LABEL_CLASS}>
          Email
        </label>
        <input
          ref={emailRef}
          id={emailId}
          type="email"
          name="email"
          autoComplete="email"
          required
          className={FIELD_CLASS}
        />
      </div>

      <div>
        <label htmlFor={messageId} className={LABEL_CLASS}>
          Message
        </label>
        <textarea
          id={messageId}
          name="message"
          rows={5}
          required
          className={`${FIELD_CLASS} resize-y`}
        />
      </div>

      {/* Reserve the widget's height so the Send button never jumps. */}
      <div ref={containerRef} className="min-h-[65px]" />

      <div aria-live="polite">
        {state.kind === 'error' && (
          <p className="text-sm font-medium text-destructive">
            {state.message}
          </p>
        )}
        {state.kind !== 'error' && status === 'error' && (
          <p className="text-sm text-foreground-muted">
            {SECURITY_CHECK_UNAVAILABLE}
          </p>
        )}
      </div>

      <button
        ref={submitRef}
        type="submit"
        disabled={sending}
        className="inline-flex min-h-12 w-full items-center justify-center rounded-md bg-primary px-7 text-base font-semibold text-foreground-on-primary hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {sending ? 'Sending…' : 'Send message'}
      </button>
    </form>
  )
}
